import { CrmDealCreationResult } from "../../pages/Company/schema/crmDealCreation";
import { ListPerson } from "../../pages/List/schema/list";

type Prospect = NonNullable<ListPerson["prospect"]>;
type CrmDeal = NonNullable<Prospect["crmDeals"]>[number];
type CrmProspect = NonNullable<Prospect["crmProspects"]>[number];
type Organization = NonNullable<Prospect["organization"]>;
type CrmOrganization = NonNullable<Organization["crmOrganizations"]>[number];

const toNullableString = (value: unknown): string | null => {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	return String(value);
};

const toNullableNumber = (value: unknown): number | null => {
	if (typeof value === "number") {
		return value;
	}

	if (typeof value !== "string" || value.trim() === "") {
		return null;
	}

	const numericValue = Number(value);

	return Number.isNaN(numericValue) ? null : numericValue;
};

const firstString = (...values: unknown[]): string | null => {
	for (const value of values) {
		const stringValue = toNullableString(value);

		if (stringValue) {
			return stringValue;
		}
	}

	return null;
};

const upsertById = <T extends { id: number | null }>(
	records: T[] | null | undefined,
	record: T
): T[] => {
	const nextRecords = [...(records ?? [])];
	const index = nextRecords.findIndex((existingRecord) => existingRecord.id === record.id);

	if (index === -1) {
		return [...nextRecords, record];
	}

	nextRecords[index] = { ...nextRecords[index], ...record };

	return nextRecords;
};

const toCrmDeal = ({ crmDeal }: CrmDealCreationResult): CrmDeal =>
	({
		id: crmDeal.id,
		dealName: crmDeal.dealName ?? null,
		pipeline: crmDeal.pipeline ?? null,
		dealStage: crmDeal.dealStage ?? null,
		lastUpdatedAt: crmDeal.lastUpdatedAt ?? null,
		dealOwner: crmDeal.dealOwner ?? null,
		dealOwnerId: crmDeal.dealOwnerId ?? null,
		closeDate: crmDeal.closeDate ?? null,
		url: crmDeal.url ?? null,
		referenceId: crmDeal.referenceId,
		createdAt: crmDeal.createdAt,
		updatedAt: crmDeal.updatedAt,
		companyId: crmDeal.companyId,
		hubspotSyncStatus: crmDeal.syncStatus ?? null,
		hubspotSyncError: crmDeal.syncError ?? null,
	}) as CrmDeal;

const toCrmProspect = ({ crmProspect }: CrmDealCreationResult): CrmProspect | null => {
	if (!crmProspect) {
		return null;
	}

	return {
		id: crmProspect.id,
		currentStatus: crmProspect.currentStatus ?? null,
		lastUpdatedAt: crmProspect.lastUpdatedAt ?? null,
		accountOwner: crmProspect.accountOwner ?? null,
		accountOwnerId: toNullableNumber(crmProspect.accountOwnerId),
		url: crmProspect.url ?? null,
		referenceId: firstString(
			crmProspect.referenceId,
			crmProspect["hubspotContactId"],
			crmProspect["zohoContactId"],
			crmProspect["zohoLeadId"],
			crmProspect["pipedrivePersonId"],
			crmProspect["pipedriveLeadId"],
			crmProspect["msdynamicsContactId"],
			crmProspect["msdynamicsLeadId"],
			crmProspect["salesforceContactId"],
			crmProspect["salesforceLeadId"]
		),
		customField1: crmProspect.customField1 ?? null,
		customField2: crmProspect.customField2 ?? null,
	};
};

const toCrmOrganization = ({ crmOrganization }: CrmDealCreationResult): CrmOrganization | null => {
	if (!crmOrganization) {
		return null;
	}

	return {
		id: crmOrganization.id,
		lastUpdatedAt: crmOrganization.lastUpdatedAt ?? null,
		accountOwner: crmOrganization.accountOwner ?? null,
		accountOwnerId: toNullableNumber(crmOrganization.accountOwnerId),
		url: crmOrganization.url ?? null,
		referenceId: firstString(
			crmOrganization.referenceId,
			crmOrganization["hubspotCompanyId"],
			crmOrganization["zohoAccountId"],
			crmOrganization["pipedriveOrganizationId"],
			crmOrganization["msdynamicsAccountId"],
			crmOrganization["salesforceAccountId"]
		),
		customField1: crmOrganization.customField1 ?? null,
		customField2: crmOrganization.customField2 ?? null,
	};
};

export const hydrateListPersonsWithCrmDeal = (
	listPersons: ListPerson[],
	dealCreationResult: CrmDealCreationResult
) => {
	const prospectId =
		dealCreationResult.crmProspect?.prospectId ?? dealCreationResult.crmDeal.prospectId;
	const crmDeal = toCrmDeal(dealCreationResult);
	const crmProspect = toCrmProspect(dealCreationResult);
	const crmOrganization = toCrmOrganization(dealCreationResult);

	for (const listPerson of listPersons) {
		const prospect = listPerson.prospect;

		if (!prospect || prospect.id !== prospectId) {
			continue;
		}

		prospect.crmDeals = upsertById(prospect.crmDeals, crmDeal);

		if (crmProspect) {
			prospect.crmProspects = upsertById(prospect.crmProspects, crmProspect);
		}

		const organization = prospect.organization;

		if (
			!crmOrganization ||
			!organization ||
			organization.id !== dealCreationResult.crmOrganization?.organizationId
		) {
			continue;
		}

		organization.crmOrganizations = upsertById(organization.crmOrganizations, crmOrganization);
	}
};

export const hydrateListPersonCollectionsWithCrmDeal = (
	state: { inbox: ListPerson[]; searchResults: ListPerson[]; campaign: ListPerson[] },
	dealCreationResult: CrmDealCreationResult
) => {
	hydrateListPersonsWithCrmDeal(state.inbox, dealCreationResult);
	hydrateListPersonsWithCrmDeal(state.searchResults, dealCreationResult);
	hydrateListPersonsWithCrmDeal(state.campaign, dealCreationResult);
};
